import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import PageTransition from '@/components/PageTransition';
import { buildAppPath } from '@/utils/tenant';
import { useSelector, useDispatch } from 'react-redux';
import {
  FiHome,
  FiUsers,
  FiMail,
  FiSettings,
  FiBarChart2,
  FiLogOut
} from 'react-icons/fi';
import { ToastContainer } from 'react-toastify';
import { selectUser, selectIsAuthenticated, selectAuthType } from '../redux/slices/authSlice';
import { logout } from '../redux/slices/authSlice';
import { company_logo_app, company_logo } from '../constants/constant';
import styles from './platform-layout.module.css';

const PlatformLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authType = useSelector(selectAuthType);
  const formattedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Additional security check - redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      console.warn('Unauthenticated access attempted to PlatformLayout');
      navigate(buildAppPath('/platform/login'), { replace: true });
    } else {
      const isPlatformAdmin =
        user?.role === 'SUPER_ADMIN' ||
        user?.role === 'superAdmin' ||
        user?.role === 'platform_admin' ||
        user?.role === 'platform_super_admin' ||
        authType === 'platform';

      if (!isPlatformAdmin) {
        console.warn('Non-platform admin access attempted:', user?.role);
        navigate(buildAppPath('/platform/login'), { replace: true });
      }
    }
  }, [isAuthenticated, authType, user, navigate]);

  useEffect(() => {
    // Load Satoshi font for platform pages via a <link> to a static CSS file
    // or inject @font-face rules. These are static declarations, not user input.
    const styleEl = document.createElement('style');
    styleEl.textContent = [
      "@font-face { font-family: 'Satoshi'; src: url('/fonts/Satoshi-Regular.otf') format('opentype'); font-weight: 400; font-style: normal; font-display: swap; }",
      "@font-face { font-family: 'Satoshi'; src: url('/fonts/Satoshi-Medium.otf') format('opentype'); font-weight: 500; font-style: normal; font-display: swap; }",
      "@font-face { font-family: 'Satoshi'; src: url('/fonts/Satoshi-Bold.otf') format('opentype'); font-weight: 700; font-style: normal; font-display: swap; }",
    ].join('\n');
    document.head.appendChild(styleEl);

    return () => {
      if (document.head.contains(styleEl)) {
        document.head.removeChild(styleEl);
      }
    };
  }, []);

  const menuItems = [
    { path: '/platform/dashboard', label: 'Dashboard', icon: FiHome },
    { path: '/platform/companies', label: 'Companies', icon: FiUsers },
    { path: '/platform/invitations', label: 'Invitations', icon: FiMail },
    { path: '/platform/analytics', label: 'Analytics', icon: FiBarChart2 },
    { path: '/platform/settings', label: 'Settings', icon: FiSettings },
  ];

  const handleLogout = async () => {
    try {
      await dispatch(logout({ redirect: true, redirectTo: '/platform/login' })).unwrap();
    } catch (error) {
      console.error('Platform logout failed:', error);
      navigate(buildAppPath('/platform/login'));
    }
  };

  const sidebarClass = sidebarOpen ? styles.sidebarExpanded : styles.sidebarCollapsed;
  const logoSectionClass = sidebarOpen ? styles.logoSectionExpanded : styles.logoSectionCollapsed;
  const logoBgClass = sidebarOpen ? styles.logoBgExpanded : styles.logoBgCollapsed;

  return (
    <div className={styles.layout}>
      {/* Sidebar */}
      <div className={`${styles.sidebar} ${sidebarClass}`}>
        {/* Collapse/Expand Arrow */}
        <button
          className={styles.collapseButton}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <svg
            width="8"
            height="10"
            viewBox="0 0 8 12"
            fill="none"
            className={`${styles.collapseIcon} ${sidebarOpen ? '' : 'rotate-180'}`}
          >
            <path
              d="M7 1L2 6L7 11"
              stroke="var(--platform-sidebar-mid, #0630AD)"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className={logoSectionClass}>
            <div className={logoBgClass}>
              <img
                src={sidebarOpen ? (company_logo_app || '/Logo.png') : (company_logo || '/Logo.png')}
                alt="MIRA"
                className={`${sidebarOpen ? 'h-8 w-auto' : 'h-6 w-auto'} transition-all duration-300`}
              />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto">
            <ul>
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => {
                        const baseClass = sidebarOpen ? styles.navLinkExpanded : styles.navLinkCollapsed;
                        return `${baseClass} ${isActive ? styles.navLinkActive : ''}`;
                      }}
                    >
                      <Icon size={20} className="text-white" />
                      <span className={sidebarOpen ? styles.navLabelExpanded : styles.navLabelCollapsed}>
                        {item.label}
                      </span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Section */}
          <div className={styles.userSection}>
            <div className="flex items-center justify-between">
              <div className={sidebarOpen ? 'block' : 'hidden'}>
                <p className={styles.userName}>
                  {user?.name || 'Platform Admin'}
                </p>
                <p className={styles.userEmail}>
                  {user?.email}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className={styles.logoutButton}
                title="Logout"
              >
                <FiLogOut size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Top Bar */}
        <header className={styles.header}>
          <div className={styles.headerInner}>
            <h1 className={styles.headerTitle}>
              MIRA Platform Administration
            </h1>
            <div className="flex items-center gap-4">
              <span className={`hidden sm:block ${styles.headerDate}`}>
                {formattedDate}
              </span>
              <div className={`hidden md:flex ${styles.headerUser}`}>
                <span className={styles.headerUserName}>
                  {user?.name || user?.email || 'Platform Admin'}
                </span>
                {user?.email ? (
                  <span className={styles.headerUserEmail}>
                    {user.email}
                  </span>
                ) : null}
              </div>
              <button
                onClick={handleLogout}
                className={styles.headerLogout}
              >
                <FiLogOut aria-hidden="true" />
                <span className="hidden sm:inline">Log out</span>
                <span className="sr-only sm:hidden">Log out</span>
              </button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className={styles.contentArea}>
          <div className={styles.contentInner}>
            <PageTransition key={location.pathname}>
              <Outlet />
            </PageTransition>
          </div>
        </main>
      </div>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default PlatformLayout;
